/**
 * The Medium tier's baked light volume (lifted from the dungeon-floors
 * prototype's `dungeon-radiance.js` bake and the shared engine's
 * `engine/src/radiance.js` volume). No dynamic lights: torches, braziers,
 * lanterns, marker lights, shaft landings, room fills and moon pools are baked
 * on the CPU into a `Data3DTexture` from the floor and the theme. Each light's
 * spread is `radiance-light.ts`; the programs that sample the volume are
 * `radiance-hook.ts`.
 *
 * Texel layout: RGBA8, `W·res × 8 × H·res` texels (`res` = 4 texels per tile,
 * 3 or 2 on floors wider than 64 tiles), x east, the 8 slices up (world y
 * -0.5..3.5), z the rows north to south (world z from -H+0.5 to 0.5); byte
 * offset ((z·8 + y)·X + x)·4. RGB is baked light ÷ 3 (the shader multiplies
 * by 3), A the share of the moon and sky light that survives there. The lowest
 * slice copies the first one above the floor. The exploration mask's B
 * channel (`fog.ts`) carries the rock-top ambient share, filled here.
 */
import type { Theme } from '@ab/content-schema';
import type { Floor } from '@ab/game-core';
import * as THREE from 'three';
import type { ExplorationMask } from './fog.ts';
import { type LightSource, at, buffer, k, verticalWeight, weighLights } from './radiance-light.ts';

export type { LightSource } from './radiance-light.ts';

export type Bake = {
  readonly texture: THREE.Data3DTexture;
  readonly min: THREE.Vector3;
  readonly span: THREE.Vector3;
  readonly bytes: number;
  /** Mean moon share of the volume's outer ring, for surfaces outside it. */
  readonly edgeMoon: { value: number };
  /** Computes the voxels (idempotent); must run before the first Medium frame. */
  fill(): void;
  readonly filled: boolean;
  bakeMs: number;
  dispose(): void;
};

/** Chessboard distance from walkable ground to the rock-top ambient share (mask B). */
const fillCapShare = (floor: Floor, rules: Theme['lightRules'], mask: ExplorationMask): void => {
  const { width: W, height: H } = floor;
  const far = rules.capAmbientFarPercent / 100;
  const near = rules.capAmbientNearPercent / 100;
  const reach = rules.capAmbientReachPercent / 100;
  const dist = new Float32Array(W * H).fill(1e9);
  const queue: number[] = [];
  for (let i = 0; i < W * H; i += 1) {
    if (floor.tiles[i] !== 'wall') {
      dist[i] = 0;
      queue.push(i);
    }
  }
  for (let head = 0; head < queue.length; head += 1) {
    const i = queue[head] ?? 0;
    const x = i % W;
    const y = (i - x) / W;
    for (let dy = -1; dy <= 1; dy += 1) {
      for (let dx = -1; dx <= 1; dx += 1) {
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= W || ny >= H) continue;
        const n = ny * W + nx;
        if (at(dist, n) > at(dist, i) + 1) {
          dist[n] = at(dist, i) + 1;
          queue.push(n);
        }
      }
    }
  }
  for (let i = 0; i < W * H; i += 1) {
    const t = Math.min(1, Math.max(0, (at(dist, i) - near) / (reach - near)));
    mask.data[i * 4 + 2] = Math.round(255 * (far + (1 - far) * (1 - t * t * (3 - 2 * t))));
  }
  mask.texture.needsUpdate = true;
};

export const createBake = (floor: Floor, lights: readonly LightSource[], theme: Theme, mask: ExplorationMask): Bake => {
  const rules = theme.lightRules;
  const { width: W, height: H } = floor;
  const res = Math.max(k.minTexelsPerTile, Math.min(k.texelsPerTile, Math.floor(k.maxTexels / Math.max(W, H))));
  const X = W * res;
  const Y = k.slices;
  const Z = H * res;
  const plane = X * Z;
  const data = new Uint8Array(X * Y * Z * 4);
  const texture = new THREE.Data3DTexture(data, X, Y, Z);
  texture.format = THREE.RGBAFormat;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.unpackAlignment = 1;
  const min = new THREE.Vector3(-0.5, k.floorY, -H + 0.5);
  const span = new THREE.Vector3(W, k.height, H);
  const edgeMoon = { value: 1 };
  let filled = false;
  const bake: Bake = {
    texture,
    min,
    span,
    bytes: data.byteLength,
    edgeMoon,
    get filled() {
      return filled;
    },
    bakeMs: 0,
    fill() {
      if (filled) return;
      filled = true;
      const started = performance.now();
      fillCapShare(floor, rules, mask);
      const shade = new THREE.Color(rules.shade).multiplyScalar(rules.shadeGainPercent / 100);
      const overlap = rules.overlapPercent / 100;
      const suppress = rules.coolSuppressPercent / 100;
      const moon = 255 * (rules.moonlightPercent / 100);
      const rockMoon = rules.rockMoonPercent / 100;
      const moonEdge = rules.moonPoolEdgePercent / 100;
      const reachScale = rules.falloffScalePercent / 100;
      const U = new Float32Array(X);
      const V = new Float32Array(Z);
      for (let i = 0; i < X; i += 1) U[i] = (i + 0.5) / res;
      for (let z = 0; z < Z; z += 1) V[z] = H - (z + 0.5) / res;
      const open = new Uint8Array(plane);
      for (let z = 0; z < Z; z += 1) for (let i = 0; i < X; i += 1) open[z * X + i] = floor.tiles[Math.floor(at(V, z)) * W + Math.floor(at(U, i))] === 'wall' ? 0 : 1;
      // Distance to the nearest wall rectangle (open texels), depth into the rock mass (wall texels).
      const near = buffer('near', plane);
      const depth = new Float32Array(W * H).fill(99);
      for (let i = 0; i < W * H; i += 1) if (floor.tiles[i] !== 'wall') depth[i] = 0;
      for (let pass = 0; pass < 2; pass += 1) {
        for (let n = 0; n < W * H; n += 1) {
          const i = pass === 1 ? W * H - 1 - n : n;
          const x = i % W;
          const y = (i - x) / W;
          const sgn = pass === 1 ? 1 : -1;
          if (at(depth, i) === 0) continue;
          let d = at(depth, i);
          const xa = x + sgn;
          const yb = y + sgn;
          if (xa >= 0 && xa < W) d = Math.min(d, at(depth, i + sgn) + 1);
          if (yb >= 0 && yb < H) d = Math.min(d, at(depth, i + sgn * W) + 1);
          if (xa >= 0 && xa < W && yb >= 0 && yb < H) d = Math.min(d, at(depth, i + sgn * W + sgn) + Math.SQRT2);
          if (yb >= 0 && yb < H && x - sgn >= 0 && x - sgn < W) d = Math.min(d, at(depth, i + sgn * W - sgn) + Math.SQRT2);
          depth[i] = d;
        }
      }
      const solidAt = (x: number, y: number): boolean => x < 0 || y < 0 || x >= W || y >= H || floor.tiles[y * W + x] === 'wall';
      const depthAt = (x: number, y: number): number => at(depth, Math.min(H - 1, Math.max(0, y)) * W + Math.min(W - 1, Math.max(0, x)));
      for (let z = 0; z < Z; z += 1) {
        for (let i = 0; i < X; i += 1) {
          const u = at(U, i);
          const v = at(V, z);
          const n = z * X + i;
          if (at(open, n) === 1) {
            const tx = Math.floor(u);
            const ty = Math.floor(v);
            let d = 9;
            for (let yy = ty - 1; yy <= ty + 1; yy += 1) {
              for (let xx = tx - 1; xx <= tx + 1; xx += 1) {
                if (solidAt(xx, yy)) d = Math.min(d, Math.hypot(Math.max(xx - u, u - xx - 1, 0), Math.max(yy - v, v - yy - 1, 0)));
              }
            }
            near[n] = d;
            continue;
          }
          const fx = u - 0.5;
          const fy = v - 0.5;
          const x0 = Math.floor(fx);
          const y0 = Math.floor(fy);
          const ax = fx - x0;
          const ay = fy - y0;
          near[n] = Math.max(0, (depthAt(x0, y0) * (1 - ax) + depthAt(x0 + 1, y0) * ax) * (1 - ay) + (depthAt(x0, y0 + 1) * (1 - ax) + depthAt(x0 + 1, y0 + 1) * ax) * ay - 0.5);
        }
      }
      const sources = weighLights(floor, lights, { res, X, Z, U, V, open }, reachScale);
      // Contact occlusion near wall feet; rock loses moon share with depth into the mass and with height.
      const occlusion = rules.wallOcclusionPercent / 100;
      const ao = buffer('ao', plane, 1);
      const rock = buffer('rock', plane, 0);
      for (let n = 0; n < plane; n += 1) {
        if (at(open, n) === 1) {
          if (occlusion > 0) ao[n] = 1 - occlusion * (1 - Math.pow(Math.min(1, at(near, n) / k.occlusionReach), k.occlusionPower));
        } else rock[n] = Math.min(1, Math.max(0, (at(near, n) - k.rockDepth[0]) / k.rockDepth[1]));
      }
      // Moon pools raise the moon share toward room centres; the periphery keeps moonPoolEdge of it.
      const pool = buffer('pool', plane, 0);
      for (const t of sources) {
        if (t.moonPool === undefined) continue;
        const [i0, k0, bw, bh] = t.box;
        const g = t.gain || 1;
        for (let r = 0; r < bh; r += 1) {
          for (let c = 0; c < bw; c += 1) {
            const n = (k0 + r) * X + i0 + c;
            pool[n] = Math.max(at(pool, n), at(t.weights, r * bw + c) / g);
          }
        }
      }
      const ledgeMoon = rules.ledgeMoonPercent / 100;
      for (let n = 0; n < plane; n += 1) {
        let v = Math.min(1, at(pool, n));
        if (ledgeMoon > 0 && at(open, n) === 0) v = Math.max(v, 1 - Math.min(1, at(near, n) / ledgeMoon));
        pool[n] = moonEdge + (1 - moonEdge) * v;
      }
      // Warm fixture light and cool fills pool separately; where warm dominates, cool terms yield.
      const acc = buffer('acc', plane * 12);
      const wallTorchMax = rules.wallTorchMaxPercent / 100;
      const capTorch = rules.capTorchMaxPercent / 100;
      const capTorchFade = rules.capTorchFadePercent / 100;
      for (let y = 1; y < Y; y += 1) {
        const wy = min.y + ((y + 0.5) / Y) * span.y;
        const aoWeight = 1 - Math.min(1, Math.max(0, (wy - k.occlusionFade[0]) / k.occlusionFade[1]));
        const hr = Math.min(1, Math.max(0, (wy - k.highRock[0]) / k.highRock[1]));
        const high = hr * hr * (3 - 2 * hr);
        acc.fill(0);
        for (const t of sources) {
          const vw = verticalWeight(t, wy);
          if (vw < k.minVertical) continue;
          const [i0, k0, bw, bh] = t.box;
          const o = t.cool ? 6 : 0;
          const cr = t.color.r * vw;
          const cg = t.color.g * vw;
          const cb = t.color.b * vw;
          for (let r = 0; r < bh; r += 1) {
            let q = ((k0 + r) * X + i0) * 12 + o;
            let j = r * bw;
            for (let c = 0; c < bw; c += 1, q += 12, j += 1) {
              const a = at(t.weights, j);
              if (a <= 1e-4) continue;
              const lr = cr * a;
              const lg = cg * a;
              const lb = cb * a;
              acc[q] = Math.max(at(acc, q), lr);
              acc[q + 1] = Math.max(at(acc, q + 1), lg);
              acc[q + 2] = Math.max(at(acc, q + 2), lb);
              acc[q + 3] = at(acc, q + 3) + lr;
              acc[q + 4] = at(acc, q + 4) + lg;
              acc[q + 5] = at(acc, q + 5) + lb;
            }
          }
        }
        const wallMax = wy < k.floorSlice ? Infinity : wallTorchMax;
        for (let z = 0, n = 0; z < Z; z += 1) {
          let i = (z * Y + y) * X * 4;
          for (let x = 0; x < X; x += 1, n += 1, i += 4) {
            const q = n * 12;
            const kk = 1 + (at(ao, n) - 1) * aoWeight;
            const deep = at(open, n) === 1 ? 0 : Math.max(at(rock, n), high);
            const sk = kk * (1 + (rockMoon - 1) * k.rockShade * deep);
            data[i + 3] = Math.min(255, Math.max(0, Math.round(moon * kk * (1 + (rockMoon - 1) * deep) * at(pool, n))));
            const put = (r: number, g: number, b: number): void => {
              data[i] = Math.min(255, Math.max(0, Math.round(r)));
              data[i + 1] = Math.min(255, Math.max(0, Math.round(g)));
              data[i + 2] = Math.min(255, Math.max(0, Math.round(b)));
            };
            const sum = at(acc, q + 3) + at(acc, q + 4) + at(acc, q + 5) + at(acc, q + 9) + at(acc, q + 10) + at(acc, q + 11);
            if (sum === 0) {
              put((shade.r / 3) * 255 * sk, (shade.g / 3) * 255 * sk, (shade.b / 3) * 255 * sk);
              continue;
            }
            const blend = (max: number, total: number): number => max + Math.min(total - max, max * overlap);
            let w0 = blend(at(acc, q), at(acc, q + 3));
            let w1 = blend(at(acc, q + 1), at(acc, q + 4));
            let w2 = blend(at(acc, q + 2), at(acc, q + 5));
            const c0 = blend(at(acc, q + 6), at(acc, q + 9));
            const c1 = blend(at(acc, q + 7), at(acc, q + 10));
            const c2 = blend(at(acc, q + 8), at(acc, q + 11));
            const wl = w0 * 0.3 + w1 * 0.6 + w2 * 0.1;
            const yc = 1 / (1 + suppress * wl * 4);
            const sd = sk / kk / (1 + suppress * wl * 1.5);
            const s = (kk / 3) * 255;
            if (wl > wallMax) {
              const f = wallMax / wl;
              w0 *= f;
              w1 *= f;
              w2 *= f;
            } else if (at(open, n) === 0) {
              const d = Math.min(1, at(near, n) / capTorchFade);
              const lim = capTorch * (1 - d * d * (3 - 2 * d));
              if (wl > lim) {
                const f = lim / wl;
                w0 *= f;
                w1 *= f;
                w2 *= f;
              }
            }
            put((shade.r * sd + w0 + c0 * yc) * s, (shade.g * sd + w1 + c1 * yc) * s, (shade.b * sd + w2 + c2 * yc) * s);
          }
        }
      }
      for (let z = 0; z < Z; z += 1) {
        const row = z * Y * X * 4;
        data.copyWithin(row, row + X * 4, row + 2 * X * 4);
      }
      let sum = 0;
      let count = 0;
      for (const y of [1, 2]) {
        for (let z = 0; z < Z; z += 1) {
          for (let x = 0; x < X; x += 1) {
            if (z === 0 || x === 0 || z === Z - 1 || x === X - 1) {
              sum += at(data, ((z * Y + y) * X + x) * 4 + 3);
              count += 1;
            }
          }
        }
      }
      edgeMoon.value = count > 0 ? sum / count / 255 : 1;
      texture.needsUpdate = true;
      bake.bakeMs = performance.now() - started;
    },
    dispose() {
      texture.dispose();
    },
  };
  return bake;
};
